import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { ApiService } from "./api.service";

export interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  imageUrl: string | string[]; // Puede ser string o array de strings
  category: string;
  brand: string;
  gender: string;
  type: string;
  subcategory: string;
  sizes: {
    valor: string;
    colors: {
      color: string;
      cant: number;
    }[];
  }[];
  style: string;
  stock: number;
}

export interface CartItem {
  product: Product;
  quantity: number;
  size: string;
  color: string;
}

@Injectable({
  providedIn: "root",
})
export class CartService {
  private cartItems: CartItem[] = [];
  private cartSubject = new BehaviorSubject<CartItem[]>([]);

  cart$ = this.cartSubject.asObservable();

  private get userId(): string | null {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user.id || user._id || user.userId || user.id_usuario || null;
    } catch {
      return null;
    }
  }

  constructor(private api: ApiService) {
    this.loadCart();
  }

  public loadCart() {
    const uid = this.userId;
    const userCartKey = uid ? `cart_${uid}` : null;
    const rawSaved = userCartKey ? (localStorage.getItem(userCartKey) || localStorage.getItem("cart")) : localStorage.getItem("cart");

    if (rawSaved) {
      try {
        const parsed = JSON.parse(rawSaved);
        let items: CartItem[] = [];
        let timestamp: number = 0;

        if (Array.isArray(parsed)) {
          items = parsed;
        } else if (parsed && Array.isArray(parsed.items)) {
          items = parsed.items;
          timestamp = parsed.timestamp || 0;
        }

        const ONE_DAY_MS = 24 * 60 * 60 * 1000;
        if (timestamp > 0 && (Date.now() - timestamp > ONE_DAY_MS)) {
          console.log('El carrito del usuario ha expirado por inactividad superior a 24 horas.');
          this.clearCart();
          return;
        }

        this.cartItems = items;
        this.cartSubject.next(this.cartItems);
      } catch {
        this.cartItems = [];
        this.cartSubject.next([]);
      }
    } else {
      this.cartItems = [];
      this.cartSubject.next([]);
    }

    // Sincronizar desde la Base de Datos relacional (Cart -> CartItem)
    const savedCartId = localStorage.getItem('cartId');
    const endpoint = uid
      ? `carts?filter=${encodeURIComponent(JSON.stringify({ where: { userId: uid }, include: [{ relation: 'items' }] }))}`
      : (savedCartId ? `carts/${savedCartId}?filter=${encodeURIComponent(JSON.stringify({ include: [{ relation: 'items' }] }))}` : null);

    if (endpoint) {
      this.api.get<any>(endpoint).subscribe(
        (res) => {
          const dbCart = Array.isArray(res) ? res[0] : res;
          if (dbCart) {
            if (dbCart.id) {
              localStorage.setItem('cartId', dbCart.id);
            }
            if (Array.isArray(dbCart.items) && dbCart.items.length > 0) {
              // Convertir modelos CartItem relacionales al formato local
              this.cartItems = dbCart.items.map((ci: any) => ({
                product: ci.product || { id: ci.productId, name: ci.title || 'Producto', price: ci.price, imageUrl: ci.image },
                quantity: ci.quantity,
                size: ci.size || 'Única',
                color: ci.color || 'Único'
              }));
              this.cartSubject.next(this.cartItems);
              const payload = { items: this.cartItems, timestamp: Date.now() };
              localStorage.setItem("cart", JSON.stringify(payload));
              if (uid) localStorage.setItem(`cart_${uid}`, JSON.stringify(payload));
            }
          }
        },
        (err) => {
          console.warn('No se pudo cargar el carrito relacional desde la base de datos:', err);
        }
      );
    }
  }

  private updateCart() {
    const payload = {
      items: this.cartItems,
      timestamp: Date.now()
    };
    localStorage.setItem("cart", JSON.stringify(payload));
    const uid = this.userId;
    if (uid) {
      localStorage.setItem(`cart_${uid}`, JSON.stringify(payload));
    }
    this.cartSubject.next(this.cartItems);

    // Sincronización Relacional en MongoDB: Cart -> CartItem (/carts/{id}/cart-items)
    const savedCartId = localStorage.getItem('cartId');
    const cartHeaderData: any = {
      timestamp: Date.now(),
      updatedAt: new Date().toISOString()
    };
    if (uid) cartHeaderData.userId = uid;

    if (savedCartId) {
      this.syncCartItemsRelational(savedCartId, cartHeaderData);
    } else if (uid) {
      // Buscar si el usuario autenticado ya posee un carrito existente en la base de datos
      const filter = encodeURIComponent(JSON.stringify({ where: { userId: uid } }));
      this.api.get<any[]>(`carts?filter=${filter}`).subscribe(
        (carts) => {
          if (Array.isArray(carts) && carts.length > 0 && carts[0].id) {
            const existingId = carts[0].id;
            localStorage.setItem('cartId', existingId);
            this.syncCartItemsRelational(existingId, cartHeaderData);
          } else {
            this.createNewCartInDb(cartHeaderData);
          }
        },
        () => this.createNewCartInDb(cartHeaderData)
      );
    } else {
      this.createNewCartInDb(cartHeaderData);
    }
  }

  private createNewCartInDb(cartHeaderData: any) {
    this.api.post<any>('carts', cartHeaderData).subscribe(
      (createdCart) => {
        if (createdCart && createdCart.id) {
          localStorage.setItem('cartId', createdCart.id);
          this.syncCartItemsRelational(createdCart.id, cartHeaderData);
        }
      },
      (err) => console.error('Error al crear cabecera de Cart en BD:', err)
    );
  }

  private syncCartItemsRelational(cartId: string, cartHeaderData: any) {
    // 1. Actualizar cabecera del carrito
    this.api.patch(`carts/${cartId}`, cartHeaderData).subscribe(
      () => {},
      (err) => console.warn('Error al actualizar cabecera de Cart:', err)
    );

    // 2. Limpiar items antiguos en la relación CartItem
    this.api.delete(`carts/${cartId}/cart-items`).subscribe(
      () => {
        // 3. Insertar cada CartItem relacional vinculado a cartId
        for (const item of this.cartItems) {
          const itemPayload = {
            cartId: cartId,
            productId: String(item.product.id),
            quantity: item.quantity,
            price: item.product.price,
            size: item.size || 'Única',
            color: item.color || 'Único'
          };
          this.api.post(`carts/${cartId}/cart-items`, itemPayload).subscribe(
            () => console.log(`✓ CartItem relacional guardado en BD para producto ${item.product.id}`),
            (err) => console.error('Error al crear CartItem relacional:', err)
          );
        }
      },
      (err) => {
        console.warn('Error al limpiar items de Cart:', err);
      }
    );
  }

  addToCart(product: Product, quantity: number, size: string, color: string) {
    const item = this.cartItems.find(
      (i) => i.product.id === product.id && i.size === size && i.color === color
    );

    if (item) {
      item.quantity += quantity;
    } else {
      this.cartItems.push({ product, quantity, size, color });
    }

    this.updateCart();
  }

  removeFromCart(productId: number, size: string, color: string) {
    this.cartItems = this.cartItems.filter(
      (i) => i.product.id !== productId || i.size !== size || i.color !== color
    );
    this.updateCart();
  }
  
  updateQuantity(productId: number, size: string, color: string, quantity: number) {
    const item = this.cartItems.find(
      (i) => i.product.id === productId && i.size === size && i.color === color
    );
    if (item) {
      item.quantity = quantity;
      this.updateCart();
    }
  }

  clearCart() {
    const savedCartId = localStorage.getItem('cartId');
    if (savedCartId) {
      this.api.delete(`carts/${savedCartId}`).subscribe(
        () => console.log('Carrito eliminado de BD.'),
        (err) => console.warn('Error eliminando carrito de BD:', err)
      );
    }

    this.cartItems = [];
    localStorage.removeItem("cart");
    localStorage.removeItem("cartInfo");
    localStorage.removeItem("cartId");
    const uid = this.userId;
    if (uid) {
      localStorage.removeItem(`cart_${uid}`);
    }
    this.cartSubject.next([]);
  }

  getCartItems() {
    return this.cartItems;
  }

  getSubtotal(): number {
    return this.cartItems.reduce((total, item) => {
      return total + (item.product.price * item.quantity);
    }, 0);
  }

  validateCoupon(code: string, cartSubtotal: number): Observable<any> {
    return this.api.post<any>('discounts/validate', { code, cartSubtotal });
  }

  processOrder(cart: any[]): Observable<any> {
    return this.api.post('orders', { items: cart });
  }
}
